import React from 'react';
import WorthyApi from 'lib/api/worthyApi';
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/x-png'];
import I18n from 'lib/i18n/i18n';
import { dataAutomationAttr } from "../../helpers/ApplicationHelper";

const INIT = 'init';
const FAILURE = 'failure';
const UPLOADING = 'uploading';
const FINISHED = 'finished';
const REMOVING = 'being_removed';
const MAX_UPLOAD_PHOTO = 4;
const MAX_UPLOAD_PHOTO_SUBMISSION = 8;

/**
 * Available hooks
 *
 * onPresented
 * onBeforeUpload
 * onSuccess (res)
 * onMultipleSuccess (res)
 * onFailure
 * onClick (error)
 * */

class ImageUploader extends React.Component {
    constructor(props) {
        super(props);
        const { item } = props;
        this.requests = [];
        let { limit, multiple } = props;
        const hasAtLeastOnePhoto = !!(item && item.photos && item.photos.length);

        let initStateObject = {
            hasAtLeastOnePhoto,
            preloadImages: [],
            isLoading: false
        };

        const emptyImg = {
            src: null,
            status: INIT,
            errorMessage: null
        }

        if (multiple) {
            this.max = (limit > 0 ? limit : 0) || MAX_UPLOAD_PHOTO;

            for (let i = 0; i < this.max; i++) {
                initStateObject.preloadImages.push(hasAtLeastOnePhoto ? {
                    idx: item.photos[i] ? i : Infinity,
                    src: item.photos[i],
                    remoteLink: item.photos[i],
                    status: INIT,
                    errorMessage: null
                } : { ...emptyImg, idx: i });
            }
        } else {
            initStateObject.preloadImages = [hasAtLeastOnePhoto ? {
                src: item.photos[0],
                remoteLink: item.photos[0],
                status: INIT,
                errorMessage: null
            } : { ...emptyImg, idx: 0 }]
        }

        this.state = initStateObject;
    }

    componentDidMount() {
        if (this.props.onPresented) {
            this.props.onPresented();
        }
    }

    _uploadSpecificImage = (e, stateIdx, fileInputIdx, isBunchUpload = false) => {
        const {
            item,
            onSuccess
        } = this.props;

        const data = new FormData();

        const file = e.target.files[isBunchUpload ? fileInputIdx : 0];
        if (!file) return
        const reader = new FileReader();
        const id = item.id;

        reader.readAsDataURL(file);
        data.append('file', file);
        data.append('extra[returnOrderId]', true);
        // order id is needed for ordering on the client side

        return WorthyApi.uploadImage(data, id)
            .then(res => {
                const newPreloadImages = [...this.state.preloadImages];
                newPreloadImages[stateIdx]['status'] = FINISHED;
                newPreloadImages[stateIdx]['idx'] = res.data.order_id;
                newPreloadImages[stateIdx]['remoteLink'] = res.data.path;

                this.setState({
                    hasAtLeastOnePhoto: true,
                    preloadImages: newPreloadImages
                });
                if (onSuccess) { onSuccess(res) }
            })
            .catch(err => this._handleError(err, stateIdx));
    }

    _bunchUpload = (event) => {
        const {
            item,
            onSuccess,
            onFailure
        } = this.props;
        const data = new FormData();

        const id = item.id;
        const files = event.target.files;
        if (!files) return;

        [ ...files ].forEach((file, idx) => {
            data.append(`file_${ idx }`, file);
        });

        return WorthyApi.uploadImages(data, id).then((res) => {
            this.setState({isLoading: false});
            if (onSuccess) {
                onSuccess(res);
            }

        }).catch((err) => {
            this.setState({isLoading: false});
            if (onFailure) {
                onFailure(err);
            }
        });
    };

    uploadImageNoAnimate = (event) => {
        const {
            item,
        } = this.props;
        const selectedFiles = event.target.files;

        if (!selectedFiles.length || item.photos.length + selectedFiles.length > MAX_UPLOAD_PHOTO_SUBMISSION) {
            return true;
        }

        this.setState({isLoading: true});
        this._bunchUpload(event);
    }

    _uploadImagesGenerally = (e) => {
        const {
            onBeforeUpload,
            item
        } = this.props;

        const {
            preloadImages
        } = this.state;

        if (onBeforeUpload) {
            onBeforeUpload();
        }

        const preloadImagesLength = preloadImages.filter(img => img.remoteLink).length;
        let inputIdx = 0;
        let inputIds = [];

        if (this.imageToBeReplaced) {
            // FIXME replacing doesn't work correctly, at least visually
            const imageToBeReplaced = this.imageToBeReplaced;
            const imageToBeReplacedIdx = preloadImages.findIndex(img => img.remoteLink === imageToBeReplaced);
            preloadImages[imageToBeReplacedIdx]['status'] = REMOVING;
            preloadImages[imageToBeReplacedIdx]['remoteLink'] = '';
            preloadImages[imageToBeReplacedIdx]['idx'] = Infinity;

            this.setState({ preloadImages });
            this.imageToBeReplaced = '';

            return WorthyApi.removeImage(item.id, imageToBeReplaced).then(data => {
                this.setState({ hasAtLeastOnePhoto: true });

                if (e.target.files.length > 1 && preloadImages.length < this.max + 1) {
                    // Please replace the image I've selected + upload the rest
                    let inputIdx = 0;
                    const stateIndicesForReplacement = [];
                    preloadImages.forEach((img, i) => {
                        if (!img.remoteLink) {
                            stateIndicesForReplacement.push(i);
                        }
                    });

                    for (let stateIdx = 0; stateIdx < stateIndicesForReplacement.length; stateIdx++) {
                        this._addPreloadingAnimation(e, stateIndicesForReplacement[stateIdx], inputIdx, true);
                        inputIds.push(inputIdx);
                        inputIdx += 1;
                    }

                    inputIds.reduce(async (previousPromise, inputIdx) => {
                        await previousPromise;
                        stateIdx++;
                        return this._uploadSpecificImage(e, stateIndicesForReplacement[inputIdx], inputIdx, true);
                    }, Promise.resolve());
                } else {
                    // Please replace only one image
                    const inputIdx = 0;
                    this._addPreloadingAnimation(e, imageToBeReplacedIdx, inputIdx, false);
                    this._uploadSpecificImage(e, imageToBeReplacedIdx, inputIdx, false);
                }
            });
        }

        this.setState({ hasAtLeastOnePhoto: true })

        for (let stateIdx = preloadImagesLength; stateIdx < this.max; stateIdx++) {
            this._addPreloadingAnimation(e, stateIdx, inputIdx, true);
            inputIds.push(inputIdx);
            inputIdx += 1;
        }

        let stateIdx = preloadImagesLength - 1;

        inputIds.reduce(async (previousPromise, inputIdx) => {
            await previousPromise;
            stateIdx++;
            return this._uploadSpecificImage(e, stateIdx, inputIdx, true);
        }, Promise.resolve());
    }
    _addPreloadingAnimation = (e, stateIdx, fileInputIdx) => {
        let {
            preloadImages
        } = this.state;

        const file = e.target.files[fileInputIdx];
        if (!file) return;
        const reader = new FileReader();

        reader.readAsDataURL(file);

        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            preloadImages[stateIdx]['src'] = null;
            preloadImages[stateIdx]['status'] = FAILURE;
            preloadImages[stateIdx]['errorMessage'] = I18n.getString('uploadImageWrongFormat');

            this.setState({ preloadImages });
        }

        reader.onload = (ctx => {
            return e => {
                const newPreloadImages = [...this.state.preloadImages];
                newPreloadImages[stateIdx]['src'] = e.target.result;
                newPreloadImages[stateIdx]['status'] = UPLOADING;

                ctx.setState({ preloadImages: newPreloadImages });
            };
        })(this);
    }
    _moveEmptyImagesToTheEnd = (a, b) => {
        // Move all non-empty images higher
        if (a.idx > b.idx) {
            return 1;
        }
        if (a.idx < b.idx) {
            return -1;
        }
        return 0;
    }
    _removeImage = ({ e, remoteLink, updateStateAsync = true, stateIdx }) => {
        const {
            item,
            onRemoveAll
        } = this.props;

        const {
            preloadImages
        } = this.state;

        const id = item.id;

        let i = stateIdx ? stateIdx : preloadImages.findIndex(img => {
            return [img['remoteLink'], img['src']].includes(remoteLink);
        })
        let newPreloadImages = [...preloadImages];
        newPreloadImages[i]['status'] = REMOVING;

        if (!updateStateAsync) {
            newPreloadImages[i]['remoteLink'] = '';
            newPreloadImages[i]['idx'] = Infinity;

            WorthyApi.removeImage(id, remoteLink);
            const hasAtLeastOnePhoto = newPreloadImages.some(img => !!img.src);

            if (!hasAtLeastOnePhoto && onRemoveAll) {
                onRemoveAll();
            }

            return this.setState({
                hasAtLeastOnePhoto,
                preloadImages: ([].concat(newPreloadImages)).sort(this._moveEmptyImagesToTheEnd)
            });
        } else this.setState({ preloadImages: newPreloadImages })

        return WorthyApi.removeImage(id, remoteLink)
            .then(res => {
                const newPreloadImages = [...preloadImages];
                const idx = newPreloadImages.findIndex(img => img.remoteLink === remoteLink);
                newPreloadImages[idx]['src'] = null;
                newPreloadImages[idx]['status'] = INIT;
                newPreloadImages[idx]['remoteLink'] = '';
                newPreloadImages[idx]['idx'] = Infinity;

                const hasAtLeastOnePhoto = newPreloadImages.some(img => !!img.src);

                if (!hasAtLeastOnePhoto && onRemoveAll) {
                    onRemoveAll();
                }

                this.setState({
                    hasAtLeastOnePhoto,
                    preloadImages: ([].concat(newPreloadImages)).sort(this._moveEmptyImagesToTheEnd)
                });

            }).catch(err => this._handleError(err, remoteLink));
    }

    _handleError = (err, idx) => {
        const {
            onFailure
        } = this.props;

        const {
            preloadImages
        } = this.state;

        console.error(err);

        const newPreloadImages = [...preloadImages];
        const hasAtLeastOnePhoto = preloadImages.some(img => !!img.src);
        newPreloadImages[idx]['src'] = null;
        newPreloadImages[idx]['status'] = INIT;
        newPreloadImages[idx]['remoteLink'] = '';
        newPreloadImages[idx]['idx'] = Infinity;

        this.setState({
            hasAtLeastOnePhoto,
            preloadImages: newPreloadImages
        });

        if (onFailure) { onFailure(err) }
    }

    renderSingleImageUpload = (i) => {
        const {
            className,
            optionalText,
            multiple,
            onClick
        } = this.props;


        return <div className={
            `multiple-images-uploader-shutter single-image-upload-template`
        }>
            {
                <>
                    <button
                        onClick={ (e) => { onClick ? onClick() : null }}
                        className={ `image-uploader single-upload ${this.state.isLoading ? 'upload-loader' : ''} ${ className } ${ multiple ? ' multiple ' : '' }` }>
                        <input
                            onChange={ e => {
                                e.persist();
                                this.uploadImageNoAnimate(e);
                            } }
                            accept={ ALLOWED_IMAGE_TYPES.join() }
                            type="file"
                            multiple/>
                        { optionalText && !this.state.isLoading ? <span
                            className={ `optional-text ${ dataAutomationAttr(optionalText) }` }>Add</span> : null }
                    </button>
                    {this.state.isLoading ? <div className='loader'/> : null}
                </>

            }
        </div>;

    };

    /* Param `isSet` is for list of small Uploader */
    renderImageUploader = (i) => {
        const {
            onClick,
            className,
            optionalText,
            optionalSubText,
            multiple,
            optimizedHeight,
            optimizedWidth
        } = this.props;

        const {
            preloadImages
        } = this.state;

        let IMAGE = preloadImages[i];

        const {
            status,
            src,
            remoteLink
        } = IMAGE;

        const optimizedDimension = optimizedHeight ? `h_${optimizedHeight}` :
            (optimizedWidth ? `w_${optimizedWidth}` : '');

        const inlineStyle = [UPLOADING, FINISHED].includes(status) || src ? {
            backgroundImage: `url(${ optimizedHeight ? src.replace('/upload', '/upload/' + optimizedDimension) : src })`
        } : null;

        const inputRef = React.createRef();

        const showRemoveIcon = src ? ![UPLOADING, REMOVING].includes(status) : false;

        return <div key={i} className='image-uploader-wrapper'>
            <button className={
                'image-uploader ' +
                className + ' ' +
                ([REMOVING, UPLOADING].includes(status) ? 'uploading ' : (status || '')) + ' ' +
                (src ? 'photo-preloaded ' : '')
            } style={inlineStyle}
                onClick={ (e) => { [INIT, UPLOADING].includes(status) && onClick ? onClick() : null }}>
                {
                    multiple ? <input type='file'
                      accept={ ALLOWED_IMAGE_TYPES.join() }
                      ref={ inputRef }
                      multiple
                      onChange={ e => {
                          e.persist();
                          this._uploadImagesGenerally(e)
                        }
                      }
                      /> :
                    <input type='file'
                        accept={ ALLOWED_IMAGE_TYPES.join() }
                        ref={ inputRef }
                        onChange={ e => this._uploadSpecificImage(e, i) }
                        />
                }
                <i className={`icon remove ${ showRemoveIcon ? '' : 'hide'}`}
                   onClick={ e => this._removeImage({e, remoteLink}) }
                   data-automation={ dataAutomationAttr('X-uploadPhoto') }
                   title="Remove an image"/>
                <i className={`icon replace ${src ? '' : 'hide'}`}
                   onClick={ () => {
                       this.imageToBeReplaced = remoteLink;
                       inputRef.current.click();
                   }}
                   data-automation={ dataAutomationAttr('edit-uploadPhoto') }
                   title="Replace an image"/>
                { inlineStyle ? null : <span className={`optional-text ${dataAutomationAttr(optionalText)}`}>{ optionalSubText || I18n.getString('uploadImageOptionalCapture') }</span> }
            </button>
            { [REMOVING, UPLOADING].includes(status) ? <div className='loader'/> : null }
        </div>
    }

    render() {
        const {
            multiple,
            className,
            optionalText,
            showImages
        } = this.props;

        const { preloadImages, hasAtLeastOnePhoto } = this.state;
        const isBusyWorking = preloadImages.some(img => [REMOVING, UPLOADING].includes(img.status));

        if (multiple && showImages) {
            return <div className={
                `multiple-images-uploader-shutter 
                ${ hasAtLeastOnePhoto ? ' continue' : ' start'} 
                ${ isBusyWorking ? ' avoid-interaction' : ''}`
            }>
                {
                    hasAtLeastOnePhoto && showImages ? preloadImages.map((image, idx) => this.renderImageUploader(idx)) :
                        <div className="">
                            <button className={ `image-uploader ${ className } ${ multiple ? ' multiple ': '' }` }>
                                <input
                                    onChange={ e => {
                                        e.persist();
                                        this._uploadImagesGenerally(e)
                                    }}
                                    accept={ ALLOWED_IMAGE_TYPES.join() }
                                    type="file"
                                    multiple/>
                            { optionalText ? <span className={`optional-text ${dataAutomationAttr(optionalText)}`}>{ optionalText || I18n.getString('uploadImageOptionalCapture') }</span> : null }
                            </button>
                        </div>
                }
            </div>
        } else if (!showImages) {
            return this.renderSingleImageUpload(0);
        } else return this.renderImageUploader(0);
    }
}

export default ImageUploader;
